import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { usePagination } from '@uhg-abyss/web/hooks/usePagination';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { motion } from 'framer-motion';
import { isEqual, isNil, isNull, isString, isUndefined, merge } from 'lodash';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import { Footer } from './components/Footer';
import { Header } from './components/Header';
import tableConfiguration from './includes/configuration.json';
import { Styles } from './includes/styles';

type TableColumn = {
  [key: string]: any;
  accessor: string;
  Cell?: (params: any) => ReactNode;
  Header: string;
};

type TableConfiguration = {
  [key: string]: any;
  initialColumns?: TableColumn[];
  pageSizeDefault?: number;
  pageSizeOptions?: number[];
  showCompactPagination?: boolean;
  showFooter?: boolean;
  showGoToPage?: boolean;
  showHeader?: boolean;
  showNumberedPagination?: boolean;
  showResultsCount?: boolean;
  showResultsPerPage?: boolean;
};

type TableProps = {
  columns?: TableColumn[];
  configuration?: TableConfiguration;
  error?: any;
  footer?: ReactNode;
  footerBottom?: ReactNode;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  footerTop?: ReactNode;
  header?: ReactNode;
  headerBottom?: ReactNode;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  headerTop?: ReactNode;
  isLoading?: boolean;
  noDataMessage?: ReactNode;
  requestArgs?: Record<string, any>;
  requestFunction?: (payload: any) => void;
  requestKey?: string;
  rows?: any[];
  totalPages?: number;
  totalRecords?: number;
};

/**
 * Table
 *
 * Reusable table component to be used/reused throughout various screens in the UI.
 */
export function Table({
  columns = [],
  configuration = {},
  error: errorObject = null,
  footer = null,
  footerBottom = null,
  footerLeft = null,
  footerRight = null,
  footerTop = null,
  header = null,
  headerBottom = null,
  headerLeft = null,
  headerRight = null,
  headerTop = null,
  isLoading = false,
  noDataMessage = null,
  requestArgs = {},
  requestFunction = () => {},
  requestKey = '',
  rows = [],
  totalPages = 0,
  totalRecords = 0,
}: TableProps) {
  const [initialRequest, setInitialRequest] = useState<boolean>(false);
  const [previousPayload, setPreviousPayload] = useState<Record<string, any>>({});

  const [resultsPerPage, setResultsPerPage] = useState<number>(
    configuration?.pageSizeDefault || tableConfiguration?.pageSizeDefault
  );

  const [sortBy, setSortBy] = useState<string | undefined>(requestArgs?.sort);

  const pagination = usePagination({
    pages: totalPages,
    pageSize: resultsPerPage,
    start: requestArgs?.page,
  });

  const theConfiguration: TableConfiguration = useMemo(() => {
    const mergedConfiguration = merge(
      {},
      merge({}, { ...tableConfiguration, ...{ renderSubComponent: null } }, configuration),
      {
        errorMessage: !isNil(errorObject) && (
          <Alert
            css={{
              marginLeft: 'var(--abyss-space-md)',
              marginRight: 'var(--abyss-space-md)',
            }}
            errorCode={errorObject?.error?.statusCode || errorObject?.statusCode}
            title={`API Error - ${
              errorObject?.error?.statusText || errorObject?.statusText || errorObject?.message || 'Unknown error'
            }`}
            variant="error"
          />
        ),
        initialColumns: columns || [],
        noDataMessage:
          isNull(noDataMessage) || isString(noDataMessage) ? (
            <Alert
              css={{
                marginLeft: 'var(--abyss-space-md)',
                marginRight: 'var(--abyss-space-md)',
              }}
              title={noDataMessage || 'No records found.'}
              variant="info"
            />
          ) : (
            noDataMessage
          ),
        uniqueStorageId: `table-${requestKey}`,
      }
    );

    if (!isUndefined(configuration?.pageSizeOptions)) {
      mergedConfiguration.pageSizeOptions = configuration?.pageSizeOptions;
    }

    if (!isUndefined(configuration?.pageSizeDefault)) {
      mergedConfiguration.pageSizeDefault = configuration?.pageSizeDefault;
    }

    return mergedConfiguration;
  }, [tableConfiguration, configuration, columns, errorObject, noDataMessage, requestKey]);

  const tableState = useDataTable(theConfiguration);

  tableState.state.isLoading = !!isLoading;

  const handleRequest = (payload: Record<string, any>) => {
    if (!isEqual(payload, previousPayload)) {
      setPreviousPayload(payload);
      requestFunction(payload);
    }
  };

  useEffect(() => {
    if (!initialRequest && !isNil(pagination.state.pageIndex) && !isNil(resultsPerPage) && !isNil(sortBy)) {
      (() => {
        handleRequest({
          ...requestArgs,
          ...{
            page: pagination.state.pageIndex,
            size: resultsPerPage,
            sort: sortBy,
          },
        });
        setInitialRequest(true);
      })();
    }
  }, [initialRequest, pagination.state.pageIndex, resultsPerPage, sortBy]);

  useEffect(() => {
    if (!isNil(tableState?.state?.sortBy?.[0]?.id) && !isNil(tableState?.state?.sortBy?.[0]?.desc)) {
      setSortBy(
        `${tableState?.state?.sortBy?.[0]?.id},${tableState?.state?.sortBy?.[0]?.desc === true ? 'desc' : 'asc'}`
      );
    }
  }, [tableState?.state?.sortBy]);

  useEffect(() => {
    if (initialRequest && !isNil(pagination.state.pageIndex) && !isNil(resultsPerPage) && !isNil(sortBy)) {
      (() => {
        handleRequest({
          ...requestArgs,
          ...{
            page: pagination.state.pageIndex,
            size: resultsPerPage,
            sort: sortBy,
          },
        });
      })();
    }
  }, [initialRequest, pagination.state.pageIndex, resultsPerPage, sortBy, requestArgs]);

  useEffect(() => {
    if (tableState?.rows !== rows) {
      tableState?.setColumns(columns, true);
      tableState?.setData(rows, true);
    }
  }, [tableState?.rows, rows, columns]);

  return (
    <ErrorHandler location="src/shared/ui/components/TableQuery/Table.tsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Styles>
          {!isNil(header) ? (
            header
          ) : (
            <React.Fragment>
              {theConfiguration?.showHeader === true && (
                <Header
                  {...{
                    headerBottom,
                    headerLeft,
                    headerRight,
                    headerTop,
                    pageSizeOptions: theConfiguration?.pageSizeOptions,
                    pagination,
                    resultsPerPage,
                    setResultsPerPage,
                    showCompactPagination: theConfiguration?.showCompactPagination,
                    showGoToPage: theConfiguration?.showGoToPage,
                    showNumberedPagination: theConfiguration?.showNumberedPagination,
                    showResultsCount: theConfiguration?.showResultsCount,
                    showResultsPerPage: theConfiguration?.showResultsPerPage,
                    totalResults: totalRecords,
                  }}
                />
              )}
            </React.Fragment>
          )}
          <DataTable className={isLoading ? 'isLoading' : ''} hideTitleHeader tableState={tableState} title="" />
          {!isNil(footer) ? (
            footer
          ) : (
            <React.Fragment>
              {theConfiguration?.showFooter === true && (
                <Footer
                  footerBottom={footerBottom}
                  footerLeft={footerLeft}
                  footerRight={footerRight}
                  footerTop={footerTop}
                  pagination={pagination}
                  resultsPerPage={resultsPerPage}
                  showNumberedPagination={theConfiguration?.showNumberedPagination}
                  showResultsCount={theConfiguration?.showResultsCount}
                  totalResults={totalRecords}
                />
              )}
            </React.Fragment>
          )}
        </Styles>
      </motion.div>
    </ErrorHandler>
  );
}
